import { defineStore } from "pinia";
import { ref, computed } from "vue";
import api from "@/api/axios";
import { isEmptyValue } from "@/utils/lib.js";
import i18n from "@/i18n/index.js";
import { clearAllTabStates } from "@/composables/useTabState.js";

const TOKEN_KEY = "auth:token";
const REFRESH_TOKEN_KEY = "auth:refresh_token";
const ROLE_KEY = "auth:role_id";
const USER_KEY = "auth:user";

const USER_INFO_KEY = "user_information";

const storage = typeof window !== "undefined" ? window.localStorage : null;

// Ambil data awal dari localStorage
const initialToken = storage?.getItem(TOKEN_KEY) || "";
const initialRefreshToken = storage?.getItem(REFRESH_TOKEN_KEY) || "";
const initialRole = storage?.getItem(ROLE_KEY) || "";
const initialUser = (() => {
  try {
    const saved = storage?.getItem(USER_KEY);
    return saved ? JSON.parse(saved) : null;
  } catch {
    return null;
  }
})();

//Get Permission from localStorage
const PERMISSIONS_KEY = "auth:permissions";
// const initialPermissions = (() => {
//   try {
//     const saved = storage?.getItem(PERMISSIONS_KEY);
//     return saved ? JSON.parse(saved) : [];
//   } catch {
//     return [];
//   }
// })();



export const useAuthStore = defineStore("auth", () => {
  const token = ref(initialToken);
  const refreshToken = ref(initialRefreshToken);
  const role = ref(initialRole);
  const user = ref(initialUser);
  const loading = ref(false);
  const error = ref(null);
  // const permissions = ref(initialPermissions);

  const setPermissions = (perms = []) => {
    
    // simpan hanya string code
    const codes = perms.map(p => p.code);
  permissions.value = codes;
  storage?.setItem(PERMISSIONS_KEY, JSON.stringify(codes));
  }
  
  const isAuthenticated = computed(() => Boolean(token.value));
  // const isAdmin = computed(() => role.value === "admin");
const permissions = ref([]);
const permissionsReady = ref(false);
    
const hydratePermissions = () => {
  try {
    const saved = storage?.getItem(PERMISSIONS_KEY);
    permissions.value = saved ? JSON.parse(saved) : [];
  } catch {
    permissions.value = [];
  } finally {
    permissionsReady.value = true;
  }
};

  hydratePermissions();

  // Helper: simpan ke localStorage + update header
  const setAuthData = (
    accessToken,
    refreshTokenVal,
    userData,
    roleVal = "admin"
  ) => {
    token.value = accessToken;
    refreshToken.value = refreshTokenVal;
    role.value = roleVal;
    user.value = userData;

    // Update header axios global (untuk fallback, meskipun api instance sudah handle)
    if (accessToken) {
      api.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
    }

    // Simpan ke storage
    storage?.setItem(TOKEN_KEY, accessToken || "");
    storage?.setItem(REFRESH_TOKEN_KEY, refreshTokenVal || "");
    storage?.setItem(ROLE_KEY, roleVal);
    storage?.setItem(USER_KEY, JSON.stringify(userData));

    // Simpan user_information jika ada
    if (userData?.user_information) {
      storage?.setItem(
        USER_INFO_KEY,
        JSON.stringify(userData.user_information)
      );
      if (!isEmptyValue(userData.user_information.language)) {
        storage?.setItem(
          "preferred-language",
          userData.user_information.language
        );
      }
    }
  };

  const clearSession = () => {
    token.value = "";
    refreshToken.value = "";
    role.value = "";
    user.value = null;

    delete api.defaults.headers.common.Authorization;

    storage?.removeItem(TOKEN_KEY);
    storage?.removeItem(REFRESH_TOKEN_KEY);
    storage?.removeItem(ROLE_KEY);
    storage?.removeItem(USER_KEY);
    storage?.removeItem(USER_INFO_KEY);
    storage?.removeItem(PERMISSIONS_KEY);
    storage?.removeItem("preferred-language");
    // Bersihkan state tab yang disimpan di localStorage ketika logout/clear session
    storage?.removeItem("tabStates");
    storage?.removeItem("app:openTabs");
    storage?.removeItem("app:activeTab");
    // Reset state tab yang masih tersimpan di memori agar kembali ke view awal
    clearAllTabStates();
  };

  const login = async (credentials) => {
    loading.value = true;
    error.value = null;

    try {
      const { data } = await api.post("/api/auth/login", {
        username: credentials.username,
        password: credentials.password,
      });

      const payload = data?.data || {};

      

      if (!payload.access_token) {
        throw new Error("Access token not returned from server");
      }

      // if (payload.role.name !== "admin") {
      //   throw new Error("Only admin accounts can access this portal");
      // }

      // Simpan semua data
      setAuthData(
        payload.access_token,
        payload.refresh_token,
        {
          username: credentials.username,
          role_id: Number(payload.role_id),
          role_name: payload.role_name || "",
          user_information: payload.user_information || null,
        },
        payload.role_id
      );

      await fetchMyPermissions();

      return {
        success: true,
        message: data?.message || i18n.global.t("auth.loginSuccess"),
      };
    } catch (err) {
      console.log("Error during login:", err);
      
      const msg = err?.response?.data?.message || err?.message || "Login gagal";
      error.value = msg;
      clearSession();
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const fetchMyPermissions = async () => {
    try {
      const { data } = await api.get("/api/auth/getMyPermissions");
      
      const perms = data?.permissions || [];
      setPermissions(perms);

      return perms;
    } catch (err) {
      setPermissions([]);
      throw err;
    }
  };

  if (!Array.isArray(permissions.value) || permissions.value.length === 0) {
   fetchMyPermissions()
  }

  const logout = async () => {
    try {
      if (token.value) {
        await api.post("/api/auth/logout").catch(() => {});
      }
    } finally {
      clearSession();
      if (typeof window !== "undefined") {
        window.location.hash = "";
      }
    }
  };

  return {
    // state
    token,
    refreshToken,
    role,
    user,
    loading,
    error,
    permissions,
    setPermissions,
    hydratePermissions,
    permissionsReady,
    // getters
    isAuthenticated,
    // isAdmin,

    // actions
    login,
    fetchMyPermissions,
    logout,
    clearSession,
  };
});
